import React, { useEffect, useRef, useCallback } from 'react';
import styles from './infinite-scroll-list.module.scss';
import { InfinityScrollEmptyIcon, InfinityScrollErrorIcon } from '../../../../assets/icons';

export interface InfiniteScrollListProps<T> {
  /** The array of items to render */
  items: T[];
  /** Function to render each item */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Callback when more items need to be loaded */
  onLoadMore: () => void;
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Whether items are currently being loaded */
  isLoading: boolean;
  /** Whether initial data is being fetched */
  isInitialLoading?: boolean;
  /** Error message if any */
  error?: string | null;
  /** Custom loading component */
  loadingComponent?: React.ReactNode;
  /** Custom empty state component */
  emptyComponent?: React.ReactNode;
  /** Custom error component */
  errorComponent?: React.ReactNode;
  /** Unique key extractor for each item */
  keyExtractor: (item: T, index: number) => string;
  /** Threshold in pixels before end of list to trigger load more */
  threshold?: number;
  /** Additional className for the container */
  className?: string;
  /** Optional ref to the scrolling container element for IntersectionObserver */
  containerRef?: React.RefObject<HTMLElement>;
  itemsListContainer?: string;
}

function InfiniteScrollList<T>({
  items,
  renderItem,
  onLoadMore,
  hasMore,
  isLoading,
  isInitialLoading = false,
  error = null,
  loadingComponent,
  emptyComponent,
  errorComponent,
  keyExtractor,
  threshold = 100,
  className = '',
  containerRef,
  itemsListContainer = '',
}: Readonly<InfiniteScrollListProps<T>>) {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);
  const [containerMounted, setContainerMounted] = React.useState(false);

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !isLoading) {
        onLoadMore();
      }
    },
    [hasMore, isLoading, onLoadMore]
  );

  // Track when the container element becomes available
  useEffect(() => {
    if (containerRef?.current) {
      setContainerMounted(true);
    }
  }, [containerRef, items]);

  useEffect(() => {
    const element = loadMoreRef.current;
    const container = containerRef?.current;

    if (!element) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new IntersectionObserver(handleObserver, {
      root: container || null,
      rootMargin: `${threshold}px`,
      threshold: 0,
    });

    observerRef.current = observer;
    observer.observe(element);

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleObserver, threshold, containerRef, containerMounted, items]);

  // Default loading component
  const defaultLoadingComponent = (
    <div className={styles.loadingContainer}>
      <div className={styles.spinner} />
      <span className={styles.loadingText}>Loading...</span>
    </div>
  );

  // Default empty state component
  const defaultEmptyComponent = (
    <div className={styles.emptyContainer}>
      <div className={styles.emptyIcon}>
        <InfinityScrollEmptyIcon />
      </div>
      <p className={styles.emptyText}>No items found</p>
    </div>
  );

  // Default error component
  const defaultErrorComponent = (
    <div className={styles.errorContainer}>
      <div className={styles.errorIcon}>
        <InfinityScrollErrorIcon />
      </div>
      <p className={styles.errorText}>{error || 'Something went wrong'}</p>
    </div>
  );

  // Show initial loading state
  if (isInitialLoading) {
    return (
      <div className={`${styles.listContainer} ${className}`}>
        {loadingComponent || defaultLoadingComponent}
      </div>
    );
  }

  // Show error state
  if (error && items.length === 0) {
    return (
      <div className={`${styles.listContainer} ${className}`}>
        {errorComponent || defaultErrorComponent}
      </div>
    );
  }

  // Show empty state
  if (!isLoading && items.length === 0) {
    return (
      <div className={`${styles.listContainer} ${className}`}>
        {emptyComponent || defaultEmptyComponent}
      </div>
    );
  }

  return (
    <div className={`${styles.listContainer} ${className}`}>
      <div className={`${styles.itemsList} ${itemsListContainer}`}>
        {items.map((item, index) => (
          <div key={keyExtractor(item, index)} className={styles.listItem}>
            {renderItem(item, index)}
          </div>
        ))}
      </div>

      {/* Load more trigger element */}
      <div ref={loadMoreRef} className={styles.loadMoreTrigger}>
        {isLoading && (loadingComponent || defaultLoadingComponent)}
        {!hasMore && items.length > 0 && (
          <p className={styles.endOfList}>No more items to load</p>
        )}
      </div>
    </div>
  );
}

export default InfiniteScrollList;